import { Component, OnInit } from '@angular/core';
import { HttpClient, HttpEventType, HttpProgressEvent } from '@angular/common/http';
import { environment } from '../../../../environments/environment';
import { AuthenticationService } from '../../../services/auth/authentication.service';
import { Router } from '@angular/router';
import { Gtp } from 'src/app/shared/gtp';

interface UploadedFile {
  filename: string;
  originalName: string;
  size: number;
  uploadDate: string;
  path: string;
}

interface ImportResult {
  success: boolean;
  message: string;
  importedCount?: number;
  groupsCreated?: number;
}

@Component({
  selector: 'app-importar-puntos',
  templateUrl: './importar-puntos.component.html',
  styleUrls: ['./importar-puntos.component.scss']
})
export class ImportarPuntosComponent implements OnInit {

  uploadedFiles: UploadedFile[] = [];
  selectedFile: File | null = null;
  uploadProgress = 0;
  isUploading = false;
  isImporting = false;
  importStatus = '';
  errorMessage = '';
  successMessage = '';

  private apiUrl = environment.baseUrl;

  constructor(
    private http: HttpClient,
    private authService: AuthenticationService,
    private router: Router,
    private gtp: Gtp
  ) { }

  ngOnInit(): void {
    this.loadUploadedFiles();
  }

  closeForm(): void {
    this.gtp.setAdminSidebar(true);
  }

  onFileSelected(event: any): void {
    const file = event.target.files[0];
    if (file) {
      // Validar que sea un archivo .xls o .xlsx
      const allowedExtensions = /\.(xls|xlsx)$/i;

      if (!allowedExtensions.test(file.name)) {
        this.errorMessage = 'Solo archivos .xls y .xlsx se permiten';
        return;
      }

      // Validar tamaño (10MB máximo)
      if (file.size > 10 * 1024 * 1024) {
        this.errorMessage = 'El archivo no puede ser mayor a 10MB';
        return;
      }

      this.selectedFile = file;
      this.errorMessage = '';
    }
  }

  clearFile(): void {
    this.selectedFile = null;
    const fileInput = document.getElementById('fileInput') as HTMLInputElement;
    if (fileInput) {
      fileInput.value = '';
    }
  }

  uploadFile(): void {
    if (!this.selectedFile) {
      this.errorMessage = 'Por favor selecciona un archivo';
      return;
    }

    this.isUploading = true;
    this.uploadProgress = 0;
    this.errorMessage = '';

    const formData = new FormData();
    formData.append('uploadfile', this.selectedFile); // Cambiar a 'uploadfile' como en el original

    this.http.post(`${this.apiUrl}puntos/upload`, formData, {
      reportProgress: true,
      observe: 'events',
      headers: {
        // No establecer Content-Type explícitamente para que Angular use multipart/form-data
        // Angular automáticamente establecerá el Content-Type correcto para FormData
      }
    }).subscribe({
      next: (event) => {
        if (event.type === HttpEventType.UploadProgress) {
          this.uploadProgress = Math.round(100 * event.loaded / (event.total || 1));
        } else if (event.type === HttpEventType.Response) {
          this.isUploading = false;
          this.uploadProgress = 0;
          this.selectedFile = null;
          this.successMessage = 'Archivo subido correctamente';
          this.loadUploadedFiles();

          // Limpiar el input file
          const fileInput = document.getElementById('fileInput') as HTMLInputElement;
          if (fileInput) {
            fileInput.value = '';
          }
        }
      },
      error: (error) => {
        this.isUploading = false;
        this.uploadProgress = 0;
        this.errorMessage = error.error?.message || 'Error al subir el archivo';
        console.error('Error uploading file:', error);
      }
    });
  }

  loadUploadedFiles(): void {
    this.http.get<{success: boolean, files: UploadedFile[]}>(`${this.apiUrl}puntos/files`)
      .subscribe({
        next: (response) => {
          if (response.success) {
            this.uploadedFiles = response.files;
          }
        },
        error: (error) => {
          console.error('Error loading files:', error);
          this.uploadedFiles = []; // Asegurar que la lista esté vacía
        }
      });
  }

  deleteFile(filename: string): void {
    if (confirm('¿Desea eliminar este archivo Excel?')) {
      this.http.delete(`${this.apiUrl}puntos/files/${filename}`)
        .subscribe({
          next: (response: any) => {
            if (response.success) {
              this.successMessage = 'Archivo eliminado correctamente';
              this.loadUploadedFiles();
            }
          },
          error: (error) => {
            this.errorMessage = error.error?.message || 'Error al eliminar el archivo';
            console.error('Error deleting file:', error);
          }
        });
    }
  }

  importPoints(filename: string): void {
    if (confirm('¿Desea convertir todo este archivo a puntos?')) {
      this.isImporting = true;
      this.importStatus = 'Subiendo Puntos...';
      this.errorMessage = '';

      this.http.post(`${this.apiUrl}puntos/import/${filename}`, {}, {
        responseType: 'json' // Cambiar a JSON para procesar respuesta detallada
      }).subscribe({
        next: (response: any) => {
          this.isImporting = false;
          this.importStatus = '';

          if (response.success) {
            // Mostrar resumen detallado de la importación
            const summary = `Puntos Cargados Correctamente
• Importados: ${response.importedCount || 0}
• Duplicados: ${response.skippedCount || 0}
• Errores: ${response.errorCount || 0}`;

            this.successMessage = summary;
            this.loadUploadedFiles();
          } else {
            this.errorMessage = response.message || 'Error en la importación. Verifique el archivo.';
          }
        },
        error: (error) => {
          this.isImporting = false;
          this.importStatus = '';

          // Intentar parsear la respuesta de error como JSON
          let errorMsg = 'Error al importar puntos. Favor intentarlo más tarde.';
          if (error.error && typeof error.error === 'object' && error.error.message) {
            errorMsg = error.error.message;
          }

          this.errorMessage = errorMsg;
          console.error('Error importing points:', error);
        }
      });
    }
  }

  downloadTemplate(): void {
    this.http.get(`${this.apiUrl}puntos/template`, {
      responseType: 'blob'
    }).subscribe({
      next: (blob) => {
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = 'planilla_puntos.xlsx';
        link.click();
        window.URL.revokeObjectURL(url);
      },
      error: (error) => {
        this.errorMessage = 'Error al descargar la plantilla';
        console.error('Error downloading template:', error);
      }
    });
  }


  formatFileSize(bytes: number): string {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  }

  formatDate(dateString: string): string {
    return new Date(dateString).toLocaleString('es-PY');
  }

  clearMessages(): void {
    this.errorMessage = '';
    this.successMessage = '';
    this.importStatus = '';
  }
}
